// src/components/GameGrid.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot
} from "firebase/firestore";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";

function getDiscountedPrice(price, rate) {
  return Math.round((price * (100 - rate)) / 100);
}

export default function GameGrid({
  collectionName = "gamse",
  filterPopular = false,
  filterSoon = false,
  filterGamejam = false,
  limit = 4
}) {
  const [items, setItems] = useState([]);
  const [likes, setLikes] = useState({});
  const [bannerUrls, setBannerUrls] = useState({}); // { [gameId]: HTTP URL }

  // 1) Firestore에서 아이템 불러오기
  useEffect(() => {
    let unsubscribe;

    if (filterPopular || filterSoon || filterGamejam) {
      const bannerDoc = doc(db, "data", "bannerList");
      unsubscribe = onSnapshot(bannerDoc, async (snap) => {
        if (!snap.exists()) {
          setItems([]);
          return;
        }
        const data = snap.data();
        let idList = [];

        if (filterPopular) {
          idList = Array.isArray(data.popular) ? data.popular : [];
        } else if (filterSoon) {
          idList = Array.isArray(data.soon) ? data.soon : [];
        } else if (filterGamejam) {
          idList = Array.isArray(data.gamejam) ? data.gamejam : [];
        }

        const docs = await Promise.all(
          idList.map(async (id) => {
            const gsnap = await getDoc(doc(db, "games", id));
            return gsnap.exists()
              ? { id: gsnap.id, ...gsnap.data() }
              : null;
          })
        );
        setItems(docs.filter((d) => d !== null));
      });
    } else {
      const collRef = collection(db, collectionName);
      unsubscribe = onSnapshot(collRef, (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      });
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [collectionName, filterPopular, filterSoon, filterGamejam]);

  // 2) Storage에서 배너 URL 가져오기 + localStorage 캐싱
  useEffect(() => {
    const storage = getStorage();

    items.forEach((item) => {
      // 이미 state에 URL이 있으면 무시
      if (bannerUrls[item.id]) return;

      const stored = item.bannerImageUrl || "";
      const isHttpUrl = stored.startsWith("http://") || stored.startsWith("https://");
      if (isHttpUrl) {
        // Firestore에 이미 HTTP URL로 저장되어 있으면 바로 사용
        setBannerUrls(prev => ({ ...prev, [item.id]: stored }));
        try {
          localStorage.setItem(`gameBanner_${item.id}`, stored);
        } catch {}
        return;
      }

      // localStorage 체크
      const cached = localStorage.getItem(`gameBanner_${item.id}`);
      if (cached) {
        setBannerUrls(prev => ({ ...prev, [item.id]: cached }));
        return;
      }

      // 스토리지 경로(예: "games/<id>/<id>_banner")를 getDownloadURL로 HTTP URL 얻어오고, 캐싱
      const pathToFile = stored || `games/${item.id}/${item.id}_banner`;
      const ref = storageRef(storage, pathToFile);
      getDownloadURL(ref)
        .then((url) => {
          setBannerUrls(prev => ({ ...prev, [item.id]: url }));
          try {
            localStorage.setItem(`gameBanner_${item.id}`, url);
          } catch {}
        })
        .catch((err) => {
          console.warn(`Storage에서 배너 URL 가져오기 실패: ${item.id}`, err);
        });
    });
  }, [items, bannerUrls]);

  const toggleLike = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setLikes((l) => ({ ...l, [id]: !l[id] }));
  };

  return (
    <section className="container mx-auto px-6 pb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.slice(0, limit).map((item) => {
          const price = Number(item.price) || 0;
          const rate = Math.min(Math.max(Number(item.discountRate) || 0, 0), 100);
          const disc = getDiscountedPrice(price, rate);
          const comingSoon = !!item.comingSoon;

          // 배지
          const badges = [];
          if (comingSoon) {
            badges.push({ label: "출시예정", color: "bg-purple-600" });
          }

          // 가격 표시(기존 로직 그대로)
          let priceDisplay;
          if (!comingSoon && price === 0 && rate === 0) {
            priceDisplay = <span className="text-base font-bold text-green-500">무료</span>;
          } else if (comingSoon && price === 0 && rate === 0) {
            priceDisplay = <span className="text-base font-bold text-yellow-500">출시예정</span>;
          } else if (rate > 0) {
            priceDisplay = (
              <div className="flex items-baseline space-x-1 text-base font-bold">
                <span className="text-red-600">-{rate.toLocaleString()}%</span>
                <span className="line-through text-gray-500">₩{price.toLocaleString()}</span>
                <span className="text-white">₩{disc.toLocaleString()}</span>
              </div>
            );
          } else {
            priceDisplay = <span className="text-base font-bold text-white">₩{price.toLocaleString()}</span>;
          }

          // bannerSrc: state로 가져온 HTTP URL
          const bannerSrc = bannerUrls[item.id] || "";

          return (
            <Link
              key={item.id}
              to={`/games/detail/${item.id}`}
              className="relative group bg-[#1f1f1f] border-2 border-neutral-600
              rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition flex flex-col"
            >
              {/* ── 배지 ── */}
              {badges.length > 0 && (
                <div className="absolute top-3 left-3 flex space-x-2 z-10">
                  {badges.map((b, i) => (
                    <span
                      key={i}
                      className={`px-2 py-1 rounded-full text-sm font-semibold text-white ${b.color}`}
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              )}

              {/* ── Hover 오버레이: 제목 + 좋아요 버튼 ── */}
              <div
                className="
                  absolute inset-0 bg-black/30 backdrop-blur-md z-20 flex flex-col
                  items-center justify-center text-center space-y-2
                  opacity-0 group-hover:opacity-100 transition-opacity
                  backdrop-filter
                "
              >
                <h3 className="text-white font-semibold text-lg truncate px-4 pb-2">
                  {item.title}
                </h3>
                <hr className="w-3/4 border-gray-600" />
                <button
                  onClick={(e) => toggleLike(item.id, e)}
                  className="p-3 bg-white/20 rounded-full hover:bg-white/30"
                >
                  {likes[item.id] ? (
                    <FaHeart className="text-red-500" />
                  ) : (
                    <FaRegHeart className="text-white" />
                  )}
                </button>
              </div>

              {/* ── 16:9 비율 배너 ── */}
              <div className="w-full aspect-banner bg-black overflow-hidden flex items-center justify-center">
                {bannerSrc ? (
                  <img
                    src={bannerSrc}
                    alt={item.title}
                    loading="lazy"
                    className="object-contain w-full h-full"
                  />
                ) : (
                  <span className="text-gray-500">이미지 없음</span>
                )}
              </div>

              {/* ── 제목 + 가격 영역 ── */}
              <div className="p-4 bg-neutral-800 flex flex-col justify-between flex-1">
                <h3 className="text-white font-bold text-xl truncate mb-8">
                  {item.title}
                </h3>
                <div className="flex items-center justify-end">
                  {priceDisplay}
                </div>
              </div>
            </Link>
          );
        })}

        {items.length === 0 && (
          <p className="col-span-full text-center text-gray-400">
            해당 조건의 게임이 없습니다.
          </p>
        )}
      </div>
    </section>
  );
}
